<template>
  <div class="abacus-frame">
    <div class="abacus"
         role="grid"
         aria-label="Mayan abacus, 10 columns of vigesimal positional notation">
      <div v-for="col in cols"
           :key="'c-' + col"
           class="column"
           role="row"
           :style="{ animationDelay: ((col - 1) * 40) + 'ms' }">

        <!-- Top section: 3 beads, each worth 5 -->
        <div class="bar bar-top" role="gridcell">
          <span v-for="row in topRows" :key="'t-' + row" class="bead-slot">
            <item :bar="top" :row="row" :col="col">5</item>
          </span>
        </div>

        <!-- Henequen rope divider -->
        <div class="henequen" aria-hidden="true">
          <span class="henequen-knot"></span>
        </div>

        <!-- Bottom section: 4 beads, each worth 1 -->
        <div class="bar bar-bottom" role="gridcell">
          <span v-for="row in bottomRows" :key="'b-' + row" class="bead-slot">
            <item :bar="bottom" :row="row" :col="col">1</item>
          </span>
        </div>

        <!-- Mayan glyph readout for this column (bars = 5, dots = 1) -->
        <div class="glyph" aria-hidden="true">
          <div class="glyph-dots">
            <span v-for="n in colDots(col)" :key="'d-' + n" class="glyph-dot"></span>
          </div>
          <div class="glyph-bars">
            <span v-for="n in colBars(col)" :key="'gb-' + n" class="glyph-bar"></span>
          </div>
        </div>

        <!-- Roman-numeral position label (power of 20) -->
        <div class="position-label" aria-hidden="true">{{ roman(col) }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import Item from './Item'

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X']

export default {
  name: 'abacus',
  components: {
    Item
  },
  data () {
    return {
      top: 'top',
      bottom: 'bottom'
    }
  },
  computed: {
    ...mapState({
      cols: state => state.cols,
      topRows: state => state.abacus.top.length,
      bottomRows: state => state.abacus.bottom.length,
      topMatrix: state => state.abacus.top,
      bottomMatrix: state => state.abacus.bottom
    })
  },
  methods: {
    roman (col) {
      return ROMAN[col - 1] || ''
    },
    // Vigesimal value at a given column (0..19)
    colValue (col) {
      let c = col - 1
      let topActive = 0
      for (let r = 0; r < this.topMatrix.length; r++) {
        if (this.topMatrix[r][c]) topActive++
      }
      let bottomActive = 0
      // In bottom bar, "active" (rendered) means matrix is false (see Item.vue)
      for (let r = 0; r < this.bottomMatrix.length; r++) {
        if (!this.bottomMatrix[r][c]) bottomActive++
      }
      return Math.min(19, topActive * 5 + bottomActive)
    },
    colBars (col) {
      return Math.floor(this.colValue(col) / 5)
    },
    colDots (col) {
      return this.colValue(col) % 5
    }
  }
}
</script>

<style scoped>
.abacus-frame {
  position: relative;
  display: flex;
  justify-content: center;
  padding: 28px 18px;
  margin: 0 auto;
  max-width: 880px;
}

/* Deckled amate-paper panel */
.abacus-frame::before {
  content: "";
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse at 20% 10%, rgba(155, 42, 37, 0.05), transparent 55%),
    radial-gradient(ellipse at 80% 90%, rgba(155, 42, 37, 0.04), transparent 55%),
    var(--amate, #efe2c4);
  border: 1px solid var(--amate-shadow, rgba(120, 85, 45, 0.35));
  border-radius: 6px;
  box-shadow:
    0 2px 0 rgba(255, 250, 235, 0.4) inset,
    0 -2px 0 rgba(120, 85, 45, 0.08) inset,
    0 12px 40px -18px rgba(40, 25, 10, 0.55),
    0 4px 12px -8px rgba(40, 25, 10, 0.25);
  /* Deckled, irregular edge */
  -webkit-mask-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100' preserveAspectRatio='none'><path d='M2,3 Q5,1 9,2 T17,3 T26,2 T34,3 T43,2 T52,3 T61,2 T70,3 T79,2 T88,3 T98,3 L98,97 Q95,99 91,98 T82,97 T73,98 T64,97 T55,98 T46,97 T37,98 T28,97 T19,98 T10,97 T2,97 Z' fill='black'/></svg>");
          mask-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100' preserveAspectRatio='none'><path d='M2,3 Q5,1 9,2 T17,3 T26,2 T34,3 T43,2 T52,3 T61,2 T70,3 T79,2 T88,3 T98,3 L98,97 Q95,99 91,98 T82,97 T73,98 T64,97 T55,98 T46,97 T37,98 T28,97 T19,98 T10,97 T2,97 Z' fill='black'/></svg>");
  -webkit-mask-size: 100% 100%;
          mask-size: 100% 100%;
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  z-index: 0;
}

/* Faint ink-trace inner frame */
.abacus-frame::after {
  content: "";
  position: absolute;
  inset: 14px;
  border: 1px solid var(--ink-trace, rgba(60, 40, 20, 0.18));
  border-radius: 3px;
  pointer-events: none;
  z-index: 0;
}

.abacus {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 6px;
  width: 100%;
  padding: 18px 8px 10px;
}

.column {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  padding: 4px 0 6px;
  opacity: 0;
  transform: translateY(8px);
  animation: column-enter 600ms ease-out forwards;
}

/* Subtle vertical "thread" line behind each column */
.column::before {
  content: "";
  position: absolute;
  top: 8px;
  bottom: 38px;
  left: 50%;
  width: 1px;
  background: linear-gradient(to bottom,
    transparent 0%,
    var(--ink-trace, rgba(60, 40, 20, 0.22)) 12%,
    var(--ink-trace, rgba(60, 40, 20, 0.22)) 88%,
    transparent 100%);
  transform: translateX(-0.5px);
  z-index: 0;
  pointer-events: none;
}

.bar {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.bar-top {
  min-height: 132px;
  justify-content: flex-start;
  padding-top: 4px;
}

.bar-bottom {
  min-height: 168px;
  justify-content: flex-start;
  padding-top: 4px;
}

.bead-slot {
  display: block;
}

/* Henequen rope: the horizontal cord that crosses the abacus */
.henequen {
  position: relative;
  width: 100%;
  height: 10px;
  margin: 4px 0;
  background:
    repeating-linear-gradient(
      90deg,
      var(--obsidiana-soft, #4a3a2a) 0px,
      var(--obsidiana-soft, #4a3a2a) 3px,
      rgba(74, 58, 42, 0.55) 3px,
      rgba(74, 58, 42, 0.55) 6px
    );
  border-top: 1px solid rgba(255, 245, 220, 0.25);
  border-bottom: 1px solid rgba(20, 12, 6, 0.35);
  box-shadow:
    0 1px 2px rgba(20, 12, 6, 0.25),
    inset 0 1px 0 rgba(255, 245, 220, 0.15);
  z-index: 2;
}

.henequen-knot {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 10px;
  height: 14px;
  transform: translate(-50%, -50%);
  background: radial-gradient(ellipse at 40% 35%,
                var(--cinabrio, #9b2a25),
                var(--cinabrio-deep, #6f1d18) 80%);
  border-radius: 40% 40% 50% 50% / 50% 50% 40% 40%;
  box-shadow:
    inset 0 1px 1px rgba(255, 220, 210, 0.35),
    0 1px 2px rgba(20, 8, 6, 0.4);
}

/* Mayan glyph readout (bonus) */
.glyph {
  margin-top: 10px;
  min-height: 22px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  z-index: 1;
}

.glyph-dots {
  display: flex;
  gap: 3px;
  min-height: 6px;
}

.glyph-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--obsidiana, #2a1f14);
  box-shadow: 0 1px 0 rgba(255, 245, 220, 0.18);
}

.glyph-bars {
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: center;
}

.glyph-bar {
  width: 18px;
  height: 3px;
  background: var(--obsidiana, #2a1f14);
  border-radius: 1px;
  box-shadow: 0 1px 0 rgba(255, 245, 220, 0.18);
}

/* Roman-numeral position label (power of 20) */
.position-label {
  margin-top: 6px;
  font-family: 'Cinzel', 'Cinzel Decorative', serif;
  font-variant: small-caps;
  font-size: 10px;
  letter-spacing: 0.12em;
  color: var(--ink-trace, rgba(60, 40, 20, 0.6));
  text-align: center;
  user-select: none;
  z-index: 1;
}

@keyframes column-enter {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .column {
    animation: none;
    opacity: 1;
    transform: none;
  }
}

@media (max-width: 720px) {
  .abacus {
    padding: 12px 4px 6px;
    gap: 2px;
  }
  .abacus-frame {
    padding: 18px 8px;
  }
  .glyph-bar {
    width: 14px;
  }
}
</style>



// WEBPACK FOOTER //
// Abacus.vue?1a2e0250